"use client";

import type { JsonPatch, Spec } from "@json-render/core";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { VIZ_STREAM_EVENT } from "@/lib/ai/visualization/config";
import {
  applyVizPatch,
  EMPTY_VIZ_TREE,
  parseVizPatchLine,
} from "@/lib/ai/visualization/runtime/stream-patches";
import type { VizStreamPatchPayload } from "@/lib/ai/visualization/types";

const DEFAULT_STREAM_KEY = "default";
const STREAM_FLUSH_FALLBACK_MS = 200;

type VizStreamState = Record<string, Spec>;
type VizStreamSubscriber = () => void;

type VizPatchEvent = {
  type: typeof VIZ_STREAM_EVENT;
  data: VizStreamPatchPayload;
};

export type VizDataPart = {
  type: string;
  data?: unknown;
};

type PendingStreamUpdate = {
  reset: boolean;
  patches: JsonPatch[];
};

function toStreamKey(toolCallId?: string): string {
  return toolCallId && toolCallId.length > 0 ? toolCallId : DEFAULT_STREAM_KEY;
}

function isVizStreamPatchPayload(
  value: unknown,
): value is VizStreamPatchPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as {
    patch?: unknown;
    toolCallId?: unknown;
    reset?: unknown;
  };
  if (typeof payload.patch !== "string") return false;
  if (
    payload.toolCallId !== undefined &&
    typeof payload.toolCallId !== "string"
  ) {
    return false;
  }
  if (payload.reset !== undefined && typeof payload.reset !== "boolean") {
    return false;
  }
  return true;
}

/**
 * 可視化ストリームの patch イベントを toolCall 単位で蓄積する Hook。
 *
 * @remarks
 * dataset raw は source tool output から client 側で再構成するため、このストアは
 * json-render patch のみを保持する。`reset` 指示はその toolCall の tree を
 * 空状態へ強制的に戻し、前回描画の残骸を次回 render へ持ち越さない。
 */
export function useVizStream() {
  const treesRef = useRef<VizStreamState>({});
  const subscribersRef = useRef<Map<string, Set<VizStreamSubscriber>>>(
    new Map(),
  );
  const pendingUpdatesRef = useRef<Map<string, PendingStreamUpdate>>(new Map());
  const frameIdRef = useRef<number | null>(null);
  const timeoutIdRef = useRef<number | null>(null);

  const clearScheduledFlush = useCallback(() => {
    if (typeof window === "undefined") return;
    if (frameIdRef.current !== null) {
      window.cancelAnimationFrame(frameIdRef.current);
      frameIdRef.current = null;
    }
    if (timeoutIdRef.current !== null) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const notifyKeySubscribers = useCallback((key: string) => {
    const subscribers = subscribersRef.current.get(key);
    if (!subscribers || subscribers.size === 0) return;
    subscribers.forEach((subscriber) => {
      subscriber();
    });
  }, []);

  const flushPendingUpdates = useCallback(() => {
    clearScheduledFlush();

    if (pendingUpdatesRef.current.size === 0) {
      return;
    }

    const pendingEntries = [...pendingUpdatesRef.current.entries()];
    pendingUpdatesRef.current.clear();

    let nextTrees: VizStreamState | null = null;
    const changedKeys = new Set<string>();

    pendingEntries.forEach(([key, update]) => {
      const hasExistingTree = key in treesRef.current;
      const currentTree = treesRef.current[key] ?? EMPTY_VIZ_TREE;
      let nextTree = update.reset ? EMPTY_VIZ_TREE : currentTree;

      update.patches.forEach((patch) => {
        nextTree = applyVizPatch(nextTree, patch);
      });

      const shouldPersistEmptyTree = update.reset && !hasExistingTree;
      if (nextTree === currentTree && !shouldPersistEmptyTree) {
        return;
      }

      if (!nextTrees) {
        nextTrees = { ...treesRef.current };
      }
      nextTrees[key] = nextTree;
      changedKeys.add(key);
    });

    if (!nextTrees) {
      return;
    }

    treesRef.current = nextTrees;
    changedKeys.forEach((key) => {
      notifyKeySubscribers(key);
    });
  }, [clearScheduledFlush, notifyKeySubscribers]);

  const scheduleFlush = useCallback(() => {
    if (frameIdRef.current !== null) {
      return;
    }
    if (timeoutIdRef.current !== null) {
      return;
    }

    if (typeof window === "undefined") {
      flushPendingUpdates();
      return;
    }

    frameIdRef.current = window.requestAnimationFrame(flushPendingUpdates);
    // バックグラウンドタブで RAF が停止しても排出が止まらないようにする。
    timeoutIdRef.current = window.setTimeout(
      flushPendingUpdates,
      STREAM_FLUSH_FALLBACK_MS,
    );
  }, [flushPendingUpdates]);

  const applyPatchEvent = useCallback(
    (event: VizPatchEvent) => {
      const { data } = event;
      const key = toStreamKey(data.toolCallId);
      const pendingUpdate = pendingUpdatesRef.current.get(key) ?? {
        reset: false,
        patches: [],
      };

      if (data.reset) {
        // reset は優先度が高いため、同フレーム内の既存 patch を破棄して空状態を起点にする。
        pendingUpdate.reset = true;
        pendingUpdate.patches = [];
        pendingUpdatesRef.current.set(key, pendingUpdate);
        scheduleFlush();
        return;
      }

      const patch = parseVizPatchLine(data.patch);
      if (!patch) return;

      pendingUpdate.patches.push(patch);
      pendingUpdatesRef.current.set(key, pendingUpdate);
      scheduleFlush();
    },
    [scheduleFlush],
  );

  const getTree = useCallback(
    (toolCallId?: string) => treesRef.current[toStreamKey(toolCallId)],
    [],
  );

  const subscribeTree = useCallback(
    (toolCallId: string | undefined, subscriber: VizStreamSubscriber) => {
      const key = toStreamKey(toolCallId);
      const subscribers = subscribersRef.current.get(key) ?? new Set();
      subscribers.add(subscriber);
      subscribersRef.current.set(key, subscribers);

      return () => {
        const current = subscribersRef.current.get(key);
        if (!current) return;
        current.delete(subscriber);
        if (current.size === 0) {
          subscribersRef.current.delete(key);
        }
      };
    },
    [],
  );

  /**
   * `onData` で受け取った part を可視化 patch イベントとして処理できるか判定する。
   *
   * @remarks
   * true を返した場合は呼び出し元で追加処理を打ち切れる。dataset payload は
   * ここでは扱わず、source tool output からの再構成に一本化している。
   */
  const handleDataPart = useCallback(
    (part: VizDataPart): boolean => {
      if (
        part.type === VIZ_STREAM_EVENT &&
        isVizStreamPatchPayload(part.data)
      ) {
        applyPatchEvent({
          type: VIZ_STREAM_EVENT,
          data: part.data,
        });
        return true;
      }
      return false;
    },
    [applyPatchEvent],
  );

  const reset = useCallback(() => {
    clearScheduledFlush();
    pendingUpdatesRef.current.clear();
    const currentKeys = Object.keys(treesRef.current);
    treesRef.current = {};
    currentKeys.forEach((key) => {
      notifyKeySubscribers(key);
    });
  }, [clearScheduledFlush, notifyKeySubscribers]);

  const resetFor = useCallback(
    (toolCallId?: string) => {
      const key = toStreamKey(toolCallId);
      pendingUpdatesRef.current.delete(key);
      const currentTree = treesRef.current[key] ?? EMPTY_VIZ_TREE;
      if (currentTree !== EMPTY_VIZ_TREE) {
        treesRef.current = { ...treesRef.current, [key]: EMPTY_VIZ_TREE };
        notifyKeySubscribers(key);
      }
    },
    [notifyKeySubscribers],
  );

  useEffect(() => {
    return () => {
      clearScheduledFlush();
    };
  }, [clearScheduledFlush]);

  const state = useMemo(
    () => ({
      getTree,
      applyPatchEvent,
      handleDataPart,
      reset,
      resetFor,
      subscribeTree,
    }),
    [getTree, applyPatchEvent, handleDataPart, reset, resetFor, subscribeTree],
  );

  return state;
}

/**
 * 指定 toolCall の tree 更新のみを購読し、必要なチャートだけ再描画する。
 */
export function useVizStreamTree(
  vizStream: VizStreamStateApi | undefined,
  toolCallId?: string,
) {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!vizStream) return () => {};
      return vizStream.subscribeTree(toolCallId, onStoreChange);
    },
    [vizStream, toolCallId],
  );
  const getSnapshot = useCallback(() => {
    if (!vizStream) return undefined;
    return vizStream.getTree(toolCallId);
  }, [vizStream, toolCallId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export type VizStreamStateApi = ReturnType<typeof useVizStream>;
